import React, { useState, useEffect } from 'react';
import { Modal, Btn, Input, Select, Textarea, Badge, DatePicker } from './UI';
import { fmtDate, fmtNum } from '../utils';
import * as api from '../api';

export default function QSInstallationModal({
  onClose, onSuccess, projectId, toast, boqItems, subsections, categories,
  disciplines, users, costCategories, materials, viewOnly = false, transaction = null
}) {
  const [form, setForm] = useState({ installation_date: new Date().toISOString().slice(0, 10), unit_of_measure: 'Nr' });
  const [saving, setSaving] = useState(false);
  const [boqComponents, setBoqComponents] = useState([]);

  useEffect(() => {
    if (!viewOnly && !transaction) {
      // Auto-generate installation code: INST-YYYYMMDD-XXXXX
      const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const randomStr = Math.random().toString(36).substring(2, 7).toUpperCase();
      setForm(f => ({ ...f, installation_code: `INST-${dateStr}-${randomStr}` }));
    }
  }, [viewOnly, transaction]);

  useEffect(() => {
    if (transaction) {
      const safeBoq = Array.isArray(boqItems) ? boqItems : [];
      const safeCost = Array.isArray(costCategories) ? costCategories : [];
      const safeSub = Array.isArray(subsections) ? subsections : [];
      const safeDisc = Array.isArray(disciplines) ? disciplines : [];
      const safeMat = Array.isArray(materials) ? materials : [];
      const safeUsers = Array.isArray(users) ? users : [];

      let deducedBoqItemId = transaction.boq_item_id;
      if (!deducedBoqItemId && (transaction.boq_item_code || transaction.boq_code)) {
         const code = String(transaction.boq_item_code || transaction.boq_code).trim().toLowerCase();
         const found = safeBoq.find(i => String(i.boq_code || '').trim().toLowerCase() === code);
         if (found) deducedBoqItemId = found.boq_item_id || found.id;
      }
      
      let deducedCostCatId = transaction.cost_category_id;
      if (!deducedCostCatId && transaction.cost_category) {
         const name = String(transaction.cost_category).trim().toLowerCase();
         const found = safeCost.find(c => String(c.name || '').trim().toLowerCase() === name);
         if (found) deducedCostCatId = found.cost_category_id || found.id;
      }

      let deducedSubId = transaction.dmr_sub_section_id;
      if (!deducedSubId && transaction.dmr_sub_section) {
         const name = String(transaction.dmr_sub_section).trim().toLowerCase();
         const found = safeSub.find(s => String(s.name || '').trim().toLowerCase() === name);
         if (found) deducedSubId = found.dmr_sub_section_id || found.dmr_subsection_id || found.id;
      }
      
      let deducedDiscId = transaction.discipline_id;
      if (!deducedDiscId && transaction.discipline) {
         const name = String(transaction.discipline).trim().toLowerCase();
         const found = safeDisc.find(d => String(d.name || '').trim().toLowerCase() === name);
         if (found) deducedDiscId = found.discipline_id || found.id;
      }
      
      let deducedMatId = transaction.material_id;
      if (!deducedMatId && (transaction.material_name || transaction.material)) {
         const name = String(transaction.material_name || transaction.material).trim().toLowerCase();
         const found = safeMat.find(m => String(m.name || '').trim().toLowerCase() === name || String(m.description || '').trim().toLowerCase() === name);
         if (found) deducedMatId = found.material_id || found.id;
      }

      let deducedQsId = transaction.qs_employee_id;
      if (!deducedQsId && transaction.qs_employee_name) {
         const name = String(transaction.qs_employee_name).trim().toLowerCase();
         const found = safeUsers.find(u => `${u.first_name || ''} ${u.last_name || ''}`.trim().toLowerCase() === name);
         if (found) deducedQsId = found.user_id || found.id;
      }
      
      let deducedSupId = transaction.supervisor_id;
      if (!deducedSupId && transaction.supervisor_name) {
         const name = String(transaction.supervisor_name).trim().toLowerCase();
         const found = safeUsers.find(u => `${u.first_name || ''} ${u.last_name || ''}`.trim().toLowerCase() === name);
         if (found) deducedSupId = found.user_id || found.id;
      }
      
      setForm({
        ...transaction,
        boq_item_id: deducedBoqItemId || transaction.boq_item_id || (transaction.boq_item_code || transaction.boq_code ? '_fallback_boq' : ''),
        cost_category_id: deducedCostCatId || transaction.cost_category_id || (transaction.cost_category ? '_fallback_cost' : ''),
        dmr_sub_section_id: deducedSubId || transaction.dmr_sub_section_id || (transaction.dmr_sub_section ? '_fallback_sub' : ''),
        discipline_id: deducedDiscId || transaction.discipline_id || (transaction.discipline ? '_fallback_disc' : ''),
        material_id: deducedMatId || transaction.material_id || (transaction.material_name || transaction.material ? '_fallback_mat' : ''),
        qs_employee_id: deducedQsId || transaction.qs_employee_id || (transaction.qs_employee_name ? '_fallback_qs' : ''),
        supervisor_id: deducedSupId || transaction.supervisor_id || (transaction.supervisor_name ? '_fallback_sup' : ''),
        installation_date: transaction.installation_date ? transaction.installation_date.slice(0, 10) : ''
      });
    }
  }, [transaction, boqItems, costCategories, subsections, disciplines, materials, users]);

  useEffect(() => {
    if (form.boq_item_id) {
      const selectedItem = boqItems.find(i => String(i.boq_item_id) === String(form.boq_item_id) || String(i.id) === String(form.boq_item_id));
      if (selectedItem && selectedItem.components && selectedItem.components.length > 0) {
        setBoqComponents(selectedItem.components);
      } else {
        setBoqComponents([]);
      }
    } else {
      setBoqComponents([]);
    }
  }, [form.boq_item_id, boqItems]);

  // Calculate totals
  useEffect(() => {
    if (!viewOnly) {
      const prev = parseFloat(form.previous_installed_qty) || 0;
      const curr = parseFloat(form.current_installed_qty) || 0;
      const total = prev + curr;

      if (form.total_installed_qty !== total) {
        setForm(f => ({ ...f, total_installed_qty: total }));
      }
    }
  }, [form.previous_installed_qty, form.current_installed_qty, viewOnly]);

  const saveTransaction = async () => {
    if (!form.material_id || !form.dmr_sub_section_id || !form.current_installed_qty) {
      toast.show('Please fill in required fields: Material, Sub-section, Current Installed Qty', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, project_id: projectId };
      if (String(payload.boq_item_id).startsWith('_fallback_')) payload.boq_item_id = null;
      if (String(payload.cost_category_id).startsWith('_fallback_')) payload.cost_category_id = null;
      if (String(payload.dmr_sub_section_id).startsWith('_fallback_')) payload.dmr_sub_section_id = null;
      if (String(payload.discipline_id).startsWith('_fallback_')) payload.discipline_id = null;
      if (String(payload.material_id).startsWith('_fallback_')) payload.material_id = null;
      if (String(payload.qs_employee_id).startsWith('_fallback_')) payload.qs_employee_id = null;
      if (String(payload.supervisor_id).startsWith('_fallback_')) payload.supervisor_id = null;
      if (String(payload.boq_component_id).startsWith('_fallback_')) payload.boq_component_id = null;
      
      Object.keys(payload).forEach(key => {
        if (payload[key] === '') payload[key] = null;
      });
      if (transaction && !viewOnly) {
        await api.updateDailyInstalled(transaction.daily_installed_id || transaction.id, payload);
        toast.show('Installation entry updated successfully');
      } else {
        await api.createDailyInstalled(payload);
        toast.show('Installation entry created successfully');
      }
      if (onSuccess) onSuccess(!!(transaction && !viewOnly));
    } catch (e) {
      toast.show(e.message, 'error');
    }
    setSaving(false);
  };

  const getSubName = (id) => subsections?.find(s => s.id === id)?.name || id;
  const getCatName = (id) => categories?.find(c => c.id === id)?.name || id;
  const getDisciplineName = (id) => disciplines?.find(d => d.discipline_id === id)?.name || id;
  const getUserName = (id) => {
    const u = users?.find(u => u.user_id === id || u.id === id);
    return u ? `${u.first_name} ${u.last_name}` : id;
  };
  const getCostCatName = (id) => costCategories?.find(c => c.cost_category_id === id)?.name || id;

  const getBoqItemName = (id) => {
    const itemsArray = Array.isArray(boqItems) ? boqItems : (boqItems?.data || boqItems?.items || []);
    const item = itemsArray.find(b => String(b.boq_item_id) === String(id) || String(b.id) === String(id));
    return item ? `${item.boq_code || ''} ${item.description}`.trim() : id;
  };

  const getBoqCompName = (boqId, compId) => {
    if (!compId) return '—';
    const itemsArray = Array.isArray(boqItems) ? boqItems : (boqItems?.data || boqItems?.items || []);
    const item = itemsArray.find(b => String(b.boq_item_id) === String(boqId) || String(b.id) === String(boqId));
    if (item && item.components) {
      const compsArray = Array.isArray(item.components) ? item.components : (item.components?.data || item.components?.items || []);
      const comp = compsArray.find(c => String(c.component_id) === String(compId) || String(c.id) === String(compId));
      if (comp) return comp.description || comp.name || compId;
    }
    return compId;
  };

  const modalContainerStyle = { padding: 0, overflow: 'hidden', background: '#ffffff', borderRadius: 12 };
  const headerStyle = { background: '#0b318bff', padding: '12px 24px', color: '#ffffff', position: 'relative' };
  const footerStyle = { background: '#0b318bff', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
  const sectionTitleStyle = { fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '0.05em', marginBottom: 12, textTransform: 'uppercase' };
  const inputStyle = { background: '#e2e8f0', color: '#1e293b', border: '1px solid #cbd5e1', padding: '10px 12px' };

  if (viewOnly && transaction) {
    const ViewField = ({ label, value, span }) => (
      <div style={{ display: 'flex', flexDirection: 'column', gridColumn: span ? `span ${span}` : undefined }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' }}>{label}</span>
        <div style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '9px 12px', fontSize: 13, fontWeight: 600, color: '#1e293b', minHeight: '38px', display: 'flex', alignItems: 'center', wordBreak: 'break-word' }}>
          {value !== null && value !== undefined && value !== '' ? (typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value) : '—'}
        </div>
      </div>
    );

    return (
      <Modal onClose={onClose} width={1300} hideHeader>
        <div style={modalContainerStyle}>
          <div style={headerStyle}>
            <button onClick={onClose} aria-label="Close" style={{
              position: 'absolute', top: 16, right: 16,
              background: 'var(--red)', border: '1px solid var(--red)', color: '#ffffff',
              fontSize: 18, cursor: 'pointer', lineHeight: 1, width: 32, height: 32, borderRadius: 10,
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>×</button>
            <h2 style={{ margin: '0 0 3px 0', fontSize: 15, fontWeight: 700, color: '#ffffff' }}>View Installation Entry</h2>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Badge color={transaction.status === 'DRAFT' ? 'gray' : transaction.status === 'APPROVED' ? 'green' : 'blue'}>{transaction.status}</Badge>
            </div>
          </div>

          <div style={{ padding: '24px', background: '#ffffff', maxHeight: '70vh', overflowY: 'auto', borderLeft: '1px solid black', borderRight: '1px solid black' }}>

            <div style={{ marginBottom: '32px' }}>
              <div style={sectionTitleStyle}>SECTION 1 — ITEM & CLASSIFICATION</div>
              <div className="grid-cols-3" style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', gap: '16px', border: '1px solid black' }}>
                <ViewField label="BOQ ITEM" value={getBoqItemName(transaction.boq_item_id)} />
                <ViewField label="BOQ COMPONENT" value={getBoqCompName(transaction.boq_item_id, transaction.boq_component_id)} />
                <ViewField label="COST COMPONENT" value={getCostCatName(transaction.cost_category_id)} />
                <ViewField label="SUB-SECTION" value={getSubName(transaction.dmr_sub_section_id)} />
                <ViewField label="DISCIPLINE" value={getDisciplineName(transaction.discipline_id)} />
              </div>
            </div>

            <div style={{ marginBottom: '32px' }}>
              <div style={sectionTitleStyle}>SECTION 2 — GENERAL DETAILS & LOCATION</div>
              <div className="grid-cols-3" style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', gap: '16px', border: '1px solid black' }}>
                <ViewField label="INSTALLATION DATE" value={transaction.installation_date ? fmtDate(transaction.installation_date) : ''} />
                <ViewField label="INSTALLATION CODE" value={transaction.installation_code} />
                <ViewField label="MATERIAL" value={transaction.material_name} />
                <ViewField label="BRAND NAME" value={transaction.brand_name} />
                <ViewField label="LOCATION" value={transaction.location} />
                <ViewField label="ZONE" value={transaction.zone} />
              </div>
            </div>

            <div style={{ marginBottom: '32px' }}>
              <div style={sectionTitleStyle}>SECTION 3 — QUANTITIES</div>
              <div className="grid-cols-4" style={{ background: '#f0f4f8', padding: '20px', borderRadius: '12px', gap: '16px', border: '1px solid black' }}>
                <ViewField label="UNIT OF MEASURE" value={transaction.unit_of_measure} />
                <ViewField label="BOQ QUANTITY" value={transaction.boq_quantity ? fmtNum(transaction.boq_quantity) : ''} />
                <ViewField label="PURCHASED QTY" value={transaction.purchased_qty ? fmtNum(transaction.purchased_qty) : ''} />
                <ViewField label="ISSUED QTY" value={transaction.issued_qty ? fmtNum(transaction.issued_qty) : ''} />
                <ViewField label="PREVIOUS INSTALLED QTY" value={transaction.previous_installed_qty ? fmtNum(transaction.previous_installed_qty) : ''} />
                <ViewField label="CURRENT INSTALLED QTY" value={transaction.current_installed_qty ? fmtNum(transaction.current_installed_qty) : ''} />
                <ViewField label="REMAINING QTY" value={transaction.remaining_qty ? fmtNum(transaction.remaining_qty) : ''} />
                <ViewField label="INSTALLATION %" value={transaction.installation_percentage ? fmtNum(transaction.installation_percentage) : ''} />
                <ViewField label="OFFCUT QTY" value={transaction.offcut_qty ? fmtNum(transaction.offcut_qty) : ''} />
                <ViewField label="REUSABLE OFFCUT" value={transaction.reusable_offcut_qty ? fmtNum(transaction.reusable_offcut_qty) : ''} />
                <ViewField label="DAMAGED QTY" value={transaction.damaged_qty ? fmtNum(transaction.damaged_qty) : ''} />
                <ViewField label="RETURNED QTY" value={transaction.returned_qty ? fmtNum(transaction.returned_qty) : ''} />
                <ViewField label="WASTAGE QTY" value={transaction.wastage_qty ? fmtNum(transaction.wastage_qty) : ''} />
                <ViewField label="LOST QTY" value={transaction.lost_qty ? fmtNum(transaction.lost_qty) : ''} />
                <ViewField label="UNINSTALLED ISSUED" value={transaction.uninstalled_issued_qty ? fmtNum(transaction.uninstalled_issued_qty) : ''} />
                <ViewField label="VARIANCE QTY" value={transaction.variance_qty ? fmtNum(transaction.variance_qty) : ''} />
                
                <div style={{ gridColumn: 'span 2', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-end', background: '#1e293b', borderRadius: '8px', padding: '12px 16px', color: '#fff' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em' }}>TOTAL INSTALLED QUANTITY</span>
                  <span style={{ fontSize: 24, fontWeight: 700, color: '#10b981', fontFamily: 'var(--mono)' }}>{fmtNum(transaction.total_installed_qty || 0)}</span>
                </div>
              </div>
            </div>

            <div>
              <div style={sectionTitleStyle}>SECTION 4 — VERIFICATION & REMARKS</div>
              <div className="grid-cols-3" style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', gap: '16px', border: '1px solid black' }}>
                <ViewField label="INSTALLER" value={transaction.installer_name} />
                <ViewField label="SUPERVISOR" value={getUserName(transaction.supervisor_id)} />
                <ViewField label="QS EMPLOYEE" value={getUserName(transaction.qs_employee_id)} />
                
                <ViewField label="QA/QC STATUS" value={transaction.qa_qc_status} />
                <ViewField label="INSPECTION STATUS" value={transaction.inspection_status} />
                <ViewField label="APPROVAL STATUS" value={transaction.approval_status} />
                <ViewField label="POSTED STATUS" value={transaction.posted_status} />

                {transaction.rejection_reason && (
                  <ViewField label="REJECTION REASON" value={transaction.rejection_reason} span={3} />
                )}
                
                <ViewField label="REMARKS" value={transaction.remarks} span={3} />
              </div>
            </div>
          </div>

          <div style={footerStyle}>
            <div />
            <Btn onClick={onClose} style={{ background: '#475569', color: '#fff', border: 'none' }}>Close</Btn>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} width={1300} hideHeader>
      <div style={modalContainerStyle}>

        <div style={headerStyle}>
          <button onClick={onClose} aria-label="Close" style={{
            position: 'absolute', top: 16, right: 16,
            background: 'var(--red)', border: '1px solid var(--red)', color: '#ffffff',
            fontSize: 18, cursor: 'pointer', lineHeight: 1, width: 32, height: 32, borderRadius: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>×</button>
          <h2 style={{ margin: '0 0 3px 0', fontSize: 15, fontWeight: 700, color: '#ffffff' }}>{transaction && !viewOnly ? 'Edit Installation Entry' : 'New Installation Entry'}</h2>
        </div>

        <div style={{ padding: '24px', background: '#ffffff', maxHeight: '70vh', overflowY: 'auto', borderLeft: '1px solid black', borderRight: '1px solid black' }}>

          <div style={{ marginBottom: '32px' }}>
            <div style={sectionTitleStyle}>SECTION 1 — ITEM CLASSIFICATION</div>
            <div className="grid-cols-3" style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', gap: '16px', border: '1px solid black' }}>
              <Select label="MAIN BOQ ITEM" value={form.boq_item_id || ''}
                onChange={v => setForm(f => ({ ...f, boq_item_id: v, boq_component_id: '' }))} style={inputStyle}
                options={[{ value: '', label: '— Select BOQ Item —' }, ...boqItems.map(i => ({ value: i.boq_item_id || i.id, label: `${i.boq_code || ''} — ${i.description}`.trim() }))]
                  .concat(form.boq_item_id && !boqItems.find(i => String(i.boq_item_id || i.id) === String(form.boq_item_id)) ? [{ value: form.boq_item_id, label: `${form.boq_item_code || ''} — ${form.boq_item_description || 'Unknown BOQ Item'}`.replace(/^ — /, '') }] : [])} />

              <Select label="SUB BOQ COMPONENT" value={form.boq_component_id || ''}
                onChange={v => setForm(f => ({ ...f, boq_component_id: v }))} style={inputStyle}
                disabled={boqComponents.length === 0 && !form.boq_component_id}
                options={boqComponents.length === 0 && !form.boq_component_id
                  ? [{ value: '', label: '— No Components Available —' }]
                  : [{ value: '', label: '— Select Component —' }, ...boqComponents.map(c => ({ value: c.component_id || c.id, label: c.description || c.sub_component_code }))]
                    .concat(form.boq_component_id && !boqComponents.find(c => String(c.component_id || c.id) === String(form.boq_component_id)) ? [{ value: form.boq_component_id, label: form.boq_component_name || 'Unknown Component' }] : [])} />

              <Select label="COST COMPONENT" value={form.cost_category_id || ''}
                onChange={v => setForm(f => ({ ...f, cost_category_id: v }))} style={inputStyle}
                options={[{ value: '', label: '— Select Cost Component —' }, ...(costCategories || []).map(c => ({ value: c.cost_category_id || c.id, label: c.name }))]
                  .concat(form.cost_category_id && !(costCategories || []).find(c => String(c.cost_category_id || c.id) === String(form.cost_category_id)) ? [{ value: form.cost_category_id, label: form.cost_category || 'Unknown Cost Component' }] : [])} />
              
              <Select label="SUB-SECTION" required value={form.dmr_sub_section_id || ''}
                onChange={v => setForm(f => ({ ...f, dmr_sub_section_id: v }))} style={inputStyle}
                options={[{ value: '', label: '— Select Sub-section —' }, ...(subsections||[]).map(b => ({ value: b.dmr_sub_section_id || b.dmr_subsection_id || b.id, label: b.name }))]
                  .concat(form.dmr_sub_section_id && !(subsections||[]).find(b => String(b.dmr_sub_section_id || b.dmr_subsection_id || b.id) === String(form.dmr_sub_section_id)) ? [{ value: form.dmr_sub_section_id, label: form.dmr_sub_section || 'Unknown Sub-section' }] : [])} />
              
              <Select label="DISCIPLINE" required value={form.discipline_id || ''}
                onChange={v => setForm(f => ({ ...f, discipline_id: v }))} style={inputStyle}
                options={[{ value: '', label: '— Select Discipline —' }, ...(disciplines||[]).map(d => ({ value: d.discipline_id || d.id, label: d.name }))]
                  .concat(form.discipline_id && !(disciplines||[]).find(d => String(d.discipline_id || d.id) === String(form.discipline_id)) ? [{ value: form.discipline_id, label: form.discipline || 'Unknown Discipline' }] : [])} />
            </div>
          </div>

          <div style={{ marginBottom: '32px' }}>
            <div style={sectionTitleStyle}>SECTION 2 — GENERAL DETAILS & LOCATION</div>
            <div className="grid-cols-3" style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', gap: '16px', border: '1px solid black' }}>
              <DatePicker label="INSTALLATION DATE" required value={form.installation_date || ''}
                onChange={v => setForm(f => ({ ...f, installation_date: v }))} style={inputStyle} />
                
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 'bold', color: '#000101ff', marginBottom: 4, textTransform: 'uppercase' }}>INSTALLATION CODE</label>
                <div style={{ background: '#e2e8f0', border: '2px dashed #081220ff', borderRadius: '6px', padding: '9px 12px', width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 14, fontWeight: 700, color: '#16a34a' }}>
                    {form.installation_code || 'Generating...'}
                  </span>
                </div>
              </div>
              
              <Select label="MATERIAL" required value={form.material_id || ''}
                onChange={v => setForm(f => ({ ...f, material_id: v }))} style={inputStyle}
                options={[{ value: '', label: '— Select Material —' }, ...(materials||[]).map(m => ({ value: m.material_id || m.id, label: m.name || m.description }))]
                  .concat(form.material_id && !(materials||[]).find(m => String(m.material_id || m.id) === String(form.material_id)) ? [{ value: form.material_id, label: form.material_name || form.material || 'Unknown Material' }] : [])} />
              
              <Input label="BRAND NAME" value={form.brand_name || ''}
                onChange={v => setForm(f => ({ ...f, brand_name: v }))} style={inputStyle} />
                
              <Input label="LOCATION" value={form.location || ''}
                onChange={v => setForm(f => ({ ...f, location: v }))} style={inputStyle} />
                
              <Input label="ZONE" value={form.zone || ''}
                onChange={v => setForm(f => ({ ...f, zone: v }))} style={inputStyle} />
            </div>
          </div>

          <div style={{ marginBottom: '32px' }}>
            <div style={sectionTitleStyle}>SECTION 3 — QUANTITIES</div>
            <div className="grid-cols-4" style={{ background: '#f0f4f8', padding: '20px', borderRadius: '12px', gap: '16px', border: '1px solid black' }}>
              <Select label="UNIT OF MEASURE" required value={form.unit_of_measure || 'Nr'}
                onChange={v => setForm(f => ({ ...f, unit_of_measure: v }))} style={inputStyle}
                options={['M', 'M2', 'M3', 'Nr', 'KG', 'LM', 'Tons', 'PCS', 'LS', 'Lot']
                  .concat(!['M', 'M2', 'M3', 'Nr', 'KG', 'LM', 'Tons', 'PCS', 'LS', 'Lot'].includes(form.unit_of_measure) && form.unit_of_measure ? [form.unit_of_measure] : [])
                  .map(s => ({ value: s, label: s }))} />
                
              <Input label="BOQ QUANTITY" type="number" value={form.boq_quantity || ''}
                onChange={v => setForm(f => ({ ...f, boq_quantity: v }))} style={inputStyle} />
                
              <Input label="PURCHASED QTY" type="number" value={form.purchased_qty || ''}
                onChange={v => setForm(f => ({ ...f, purchased_qty: v }))} style={inputStyle} />
                
              <Input label="ISSUED QTY" type="number" value={form.issued_qty || ''}
                onChange={v => setForm(f => ({ ...f, issued_qty: v }))} style={inputStyle} />

              <Input label="PREVIOUS INSTALLED QTY" type="number" value={form.previous_installed_qty || ''}
                onChange={v => setForm(f => ({ ...f, previous_installed_qty: v }))} style={inputStyle} />
                
              <Input label="CURRENT INSTALLED QTY" type="number" required value={form.current_installed_qty || ''}
                onChange={v => setForm(f => ({ ...f, current_installed_qty: v }))} style={inputStyle} />
                
              <Input label="REMAINING QTY" type="number" value={form.remaining_qty || ''}
                onChange={v => setForm(f => ({ ...f, remaining_qty: v }))} style={inputStyle} />
                
              <Input label="INSTALLATION %" type="number" value={form.installation_percentage || ''}
                onChange={v => setForm(f => ({ ...f, installation_percentage: v }))} style={inputStyle} />
                
              <Input label="OFFCUT QTY" type="number" value={form.offcut_qty || ''}
                onChange={v => setForm(f => ({ ...f, offcut_qty: v }))} style={inputStyle} />
                
              <Input label="REUSABLE OFFCUT QTY" type="number" value={form.reusable_offcut_qty || ''}
                onChange={v => setForm(f => ({ ...f, reusable_offcut_qty: v }))} style={inputStyle} />
                
              <Input label="DAMAGED QTY" type="number" value={form.damaged_qty || ''}
                onChange={v => setForm(f => ({ ...f, damaged_qty: v }))} style={inputStyle} />

              <Input label="RETURNED QTY" type="number" value={form.returned_qty || ''}
                onChange={v => setForm(f => ({ ...f, returned_qty: v }))} style={inputStyle} />
                
              <Input label="WASTAGE QTY" type="number" value={form.wastage_qty || ''}
                onChange={v => setForm(f => ({ ...f, wastage_qty: v }))} style={inputStyle} />
                
              <Input label="LOST QTY" type="number" value={form.lost_qty || ''}
                onChange={v => setForm(f => ({ ...f, lost_qty: v }))} style={inputStyle} />
                
              <Input label="UNINSTALLED ISSUED QTY" type="number" value={form.uninstalled_issued_qty || ''}
                onChange={v => setForm(f => ({ ...f, uninstalled_issued_qty: v }))} style={inputStyle} />
                
              <Input label="VARIANCE QTY" type="number" value={form.variance_qty || ''}
                onChange={v => setForm(f => ({ ...f, variance_qty: v }))} style={inputStyle} />

              <div style={{ gridColumn: 'span 2', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', background: '#1e293b', borderRadius: '8px', padding: '12px 16px', color: '#fff' }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em' }}>TOTAL INSTALLED QUANTITY</span>
                <span style={{ fontSize: 24, fontWeight: 700, color: '#10b981', fontFamily: 'var(--mono)' }}>{fmtNum(form.total_installed_qty || 0)}</span>
              </div>
            </div>
          </div>

          <div>
            <div style={sectionTitleStyle}>SECTION 4 — VERIFICATION & REMARKS</div>
            <div className="grid-cols-3" style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', gap: '16px', border: '1px solid black' }}>
              <Input label="INSTALLER NAME" value={form.installer_name || ''}
                onChange={v => setForm(f => ({ ...f, installer_name: v }))} style={inputStyle} />
              <Select label="SUPERVISOR" value={form.supervisor_id || ''}
                onChange={v => setForm(f => ({ ...f, supervisor_id: v }))} style={inputStyle}
                options={[{ value: '', label: '— Select Supervisor —' }, ...(users||[]).map(u => ({ value: u.user_id || u.id, label: `${u.first_name} ${u.last_name}` }))]
                  .concat(form.supervisor_id && !(users||[]).find(u => String(u.user_id || u.id) === String(form.supervisor_id)) ? [{ value: form.supervisor_id, label: form.supervisor_name || 'Unknown User' }] : [])} />
              <Select label="QS EMPLOYEE" value={form.qs_employee_id || ''}
                onChange={v => setForm(f => ({ ...f, qs_employee_id: v }))} style={inputStyle}
                options={[{ value: '', label: '— Select QS Employee —' }, ...(users||[]).map(u => ({ value: u.user_id || u.id, label: `${u.first_name} ${u.last_name}` }))]
                  .concat(form.qs_employee_id && !(users||[]).find(u => String(u.user_id || u.id) === String(form.qs_employee_id)) ? [{ value: form.qs_employee_id, label: form.qs_employee_name || 'Unknown User' }] : [])} />
                
              <Select label="QA/QC STATUS" value={form.qa_qc_status || ''}
                onChange={v => setForm(f => ({ ...f, qa_qc_status: v }))} style={inputStyle}
                options={[{ value: '', label: '— Select —' }, { value: 'Pending', label: 'Pending' }, { value: 'Approved', label: 'Approved' }, { value: 'Rejected', label: 'Rejected' }].concat(!['Pending', 'Approved', 'Rejected'].includes(form.qa_qc_status) && form.qa_qc_status ? [{value: form.qa_qc_status, label: form.qa_qc_status}] : [])} />
              <Select label="INSPECTION STATUS" value={form.inspection_status || ''}
                onChange={v => setForm(f => ({ ...f, inspection_status: v }))} style={inputStyle}
                options={[{ value: '', label: '— Select —' }, { value: 'Pending', label: 'Pending' }, { value: 'Inspected', label: 'Inspected' }, { value: 'Requires Rework', label: 'Requires Rework' }].concat(!['Pending', 'Inspected', 'Requires Rework'].includes(form.inspection_status) && form.inspection_status ? [{value: form.inspection_status, label: form.inspection_status}] : [])} />
              <Select label="APPROVAL STATUS" value={form.approval_status || ''}
                onChange={v => setForm(f => ({ ...f, approval_status: v }))} style={inputStyle}
                options={[{ value: '', label: '— Select —' }, { value: 'Draft', label: 'Draft' }, { value: 'Approved', label: 'Approved' }, { value: 'Rejected', label: 'Rejected' }].concat(!['Draft', 'Approved', 'Rejected'].includes(form.approval_status) && form.approval_status ? [{value: form.approval_status, label: form.approval_status}] : [])} />
                
              <div style={{ gridColumn: 'span 3' }}>
                <Textarea label="REMARKS" value={form.remarks || ''}
                  onChange={v => setForm(f => ({ ...f, remarks: v }))}
                  rows={3} placeholder="Optional notes or description about the installation..."
                  style={{ ...inputStyle, width: '100%' }} />
              </div>
            </div>
          </div>

        </div>

        <div style={footerStyle}>
          <Btn style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '10px 24px', fontWeight: 600, fontSize: 14 }} onClick={onClose}>
            Cancel
          </Btn>
          <Btn loading={saving} onClick={saveTransaction} style={{ background: '#10b981', color: '#fff', border: 'none', padding: '10px 24px', fontWeight: 600, fontSize: 14 }}>
            {transaction && !viewOnly ? 'Update' : 'Save'}
          </Btn>
        </div>

      </div>
    </Modal>
  );
}
